/**
 * Application Boot Initialization
 *
 * Initializes third-party integrations, services, and app state.
 * This is the "composition root" where we assemble services or singletons
 * needed throughout the app.
 */

import { MedplumClient } from "@medplum/core";
import {
  initializeDatadogRum,
  connectEventEmitterToDatadog,
} from "@/app/boot/initializeDatadog";
import { globalEventEmitter } from "@benchmark/core/events";
import { getAuthResetCallback } from "@/app/auth/performAuthReset";
import {
  clearLastActivity,
  readLastActivity,
} from "@/app/auth/lastActivityStorage";
import { LOGOUT_AT_MS } from "@/app/auth/inactivityConfig";
import { isInactivityLogoutEnabledAtBoot } from "@/utils/feature-flags";

/** Consecutive API 401s that signal the SDK is stuck in its
 *  `request ↔ handleUnauthenticated` retry loop. The SDK retries a 401
 *  indefinitely (after a no-op refresh) whenever a refresh token is present
 *  and the access token is locally unexpired but server-rejected — it never
 *  yields to `onUnauthenticated`. @agentic connections */
const MAX_CONSECUTIVE_401S = 5;

/** URL of a fetch input, normalized to a string. @agentic connections */
const fetchInputUrl = (input: RequestInfo | URL): string => {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.href;
  return input.url;
};

/**
 * Wrap `fetch` with a circuit breaker that trips after
 * {@link MAX_CONSECUTIVE_401S} consecutive 401s from the Medplum API and
 * invokes `onTripped`. Breaks the SDK's unbounded 401 retry loop, which the
 * soft-reset path cannot otherwise intercept (it never reaches
 * `onUnauthenticated`). Any non-401 API response resets the counter.
 * @agentic connections
 */
export const createCircuitBreakingFetch = (
  baseUrl: string,
  onTripped: () => void,
): typeof fetch => {
  let consecutive401s = 0;
  return async (input: RequestInfo | URL, init?: RequestInit) => {
    const response = await fetch(input, init);
    if (fetchInputUrl(input).startsWith(baseUrl)) {
      if (response.status === 401) {
        consecutive401s += 1;
        if (consecutive401s >= MAX_CONSECUTIVE_401S) {
          consecutive401s = 0;
          onTripped();
        }
      } else {
        consecutive401s = 0;
      }
    }
    return response;
  };
};

/**
 * Initialize Medplum client with app-specific config (base URL, client ID,
 * unauth redirect).
 *
 * `onUnauthenticated` prefers the soft auth-reset callback installed by
 * `<AuthResetGate>` (Phase 3). When no callback is installed yet — e.g.
 * the SDK fires before the React tree mounts, or in tests — falls back
 * to the legacy hard reload so the user is not stranded.
 *
 * The client uses a circuit-breaking `fetch`: if the SDK gets stuck retrying
 * a server-rejected-but-locally-unexpired token (the unbounded 401 loop that
 * never reaches `onUnauthenticated`), the breaker force-clears the session,
 * so the next `handleUnauthenticated` takes the `refresh()`-falsy branch and
 * the soft reset fires.
 *
 * @agentic connections
 */
export function initializeMedplumClient(): MedplumClient {
  const baseUrl =
    import.meta.env["VITE_MEDPLUM_BASE_URL"] || "https://api.medplum.com/";

  const client: MedplumClient = new MedplumClient({
    onUnauthenticated: () => {
      const callback = getAuthResetCallback();
      if (callback) {
        callback("unauthenticated");
        return;
      }
      // No callback installed yet — SDK fired before the React tree
      // mounted, or we're running in a test that didn't mount the gate.
      // Fall back to the hard reload so the user isn't stranded.
      if (typeof window !== "undefined") {
        window.location.href = "/";
      }
    },
    fetch: createCircuitBreakingFetch(baseUrl, () => {
      // Break the loop: wipe the rejected session, then run the same soft
      // reset as onUnauthenticated. Clearing alone would leave the
      // `wasAuthenticated` latch set, so `RequireAuth` would keep rendering
      // the authenticated shell (with a dead session) until some later
      // request happened to trip `onUnauthenticated`. Running the reset now
      // clears the latch and navigates to /signin immediately.
      client.clear();
      const callback = getAuthResetCallback();
      if (callback) {
        callback("unauthenticated");
      } else if (typeof window !== "undefined") {
        window.location.href = "/";
      }
    }),
    baseUrl,
    cacheTime: 60000,
    autoBatchTime: 100,
    clientId: import.meta.env["VITE_MEDPLUM_CLIENT_ID"],
  });

  // Phase 5: inactivity-after-tab-close enforcement. If the tab was
  // closed past the threshold and the user re-opens it, sign them out
  // synchronously before any authenticated UI renders. Same feature
  // flag as the in-tab inactivity logout.
  if (isInactivityLogoutEnabledAtBoot()) {
    const last = readLastActivity();
    if (
      last !== undefined &&
      Date.now() - last > LOGOUT_AT_MS &&
      client.getActiveLogin()
    ) {
      // Synchronous local teardown. `signOut()` would POST to the logout
      // endpoint *before* clearing local state — and against an already-
      // invalid token that POST loops on 401 indefinitely and never reaches
      // its own clear(). `clear()` wipes the local session immediately so
      // RequireAuth sees an unauthenticated client on first render and no
      // request carries the dead token. The server-side revoke is forgone
      // here: a stale tab-close session has nothing useful left to revoke.
      client.clear();
      clearLastActivity();
    }
  }

  return client;
}

/**
 * Initialize application boot sequence. Call this before rendering the app.
 * Feature flags are resolved lazily per-call from `@benchmark/core/feature-flags`
 * — no boot-time initialization required.
 * @agentic connections
 */
export function initializeApp(): void {
  // Initialize Datadog RUM for error tracking and performance monitoring
  initializeDatadogRum();

  // Connect the global event emitter to Datadog RUM
  // (error forwarding + domain event tracking as custom actions)
  connectEventEmitterToDatadog(globalEventEmitter);
}
